import React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import Avatar from "../@extended/Avatar";
import { PopupTransition } from "../@extended/Transitions";
import { CheckCircleFilled, StopOutlined } from "@ant-design/icons";
import { FormattedMessage } from "react-intl";

interface ReusableActivateDialogProps {
  open: boolean;
  handleClose: () => void;
  handleActivate: () => void;
  title?: string;
  isActivating: boolean;
  isActive: boolean; // current state, true = active, so dialog is for deactivation
  dialogContent?: React.ReactNode;
}

export default function ReusableActivateDialog({
  open,
  handleClose,
  handleActivate,
  title,
  isActivating,
  isActive,
  dialogContent,
}: ReusableActivateDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted
      TransitionComponent={PopupTransition}
      maxWidth="xs"
      aria-labelledby="column-activate-title"
      aria-describedby="column-activate-description"
    >
      <DialogContent sx={{ mt: 2, my: 1 }}>
        <Stack alignItems="center" spacing={3.5}>
          <Avatar
            color={isActive ? "error" : "success"}
            sx={{ width: 72, height: 72, fontSize: "1.75rem" }}
          >
            {isActive ? <StopOutlined /> : <CheckCircleFilled />}
          </Avatar>
          <Stack spacing={2}>
            <Typography variant="h4" align="center">
              {isActive ? (
                <FormattedMessage id="Are you sure you want to deactivate" />
              ) : (
                <FormattedMessage id="Are you sure you want to activate" />
              )}
            </Typography>
            {dialogContent || (
              <Typography align="center">
                <FormattedMessage id="This will" />
                {isActive ? (
                  <FormattedMessage id="deactivate this item" />
                ) : (
                  <FormattedMessage id="activate this item" />
                )}
                {title && (
                  <Typography variant="subtitle1" component="span">
                    {" "}
                    "{title}"{" "}
                  </Typography>
                )}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={2} sx={{ width: 1 }}>
            <Button
              fullWidth
              onClick={handleClose}
              color="secondary"
              variant="outlined"
              disabled={isActivating}
            >
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              fullWidth
              color={isActive ? "error" : "success"}
              variant="contained"
              onClick={handleActivate}
              autoFocus
              disabled={isActivating}
            >
              {isActive ? (
                <FormattedMessage id="Deactivate" />
              ) : (
                <FormattedMessage id="Activate" />
              )}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
